import React, { useMemo } from 'react'
import { Form } from 'semantic-ui-react'
import { Icon, Text } from '../../../elementals'
import { TextTruncate } from '../../../layouts'
import { CheckboxField } from '../../../formFields'
import { PopupTooltip } from '../../../overlays'
import * as constants from '../../../../constants'
import * as hooks from '../../../../hooks'
import classnames from 'classnames/bind'
import _ from 'lodash'
import styles from './styles.scss'
import * as graph from '@adc/sdk'
import { SlottableComponent } from '../../../../types'
import {
  getOfferAndCounterOfferDifferences,
  getSealedBidFormConfig,
  getSealedBidFormHeaders,
  getSealedBidFormValues,
  SealedBidFormConfig,
} from '../../../../utils/sealedBid'
import { CurrentTargetAudience, OnlineAuctionSegmentBid } from '@adc/sdk/types'
import { InferBoundAuctionBlueprintCollectionElement } from '@adc/blueprint/lib/primitives/types'
import { ListingsData } from '@adc/blueprint/lib/bidding/types'

const { ColorNames } = constants
interface BidFormProps extends SlottableComponent<'components'> {
  listing?: InferBoundAuctionBlueprintCollectionElement<ListingsData['mapped']>
  bid: OnlineAuctionSegmentBid
  config?: SealedBidFormConfig
  counterOffer?: OnlineAuctionSegmentBid
  className?: string
  readOnly?: boolean
  dirty?: boolean
  headers?: string[]
  onToggleReadOnly?: () => void
  onSubmit?: () => void
  viewingFrom?: CurrentTargetAudience
  formRef?: React.RefObject<HTMLFormElement>
}

const cx = classnames.bind(styles)

const ToolTip = ({ content }: { content: string }) => (
  <PopupTooltip
    offset={0}
    horizontalOffset={0}
    verticalOffset={0}
    content={content}
    wide={false}
    trigger={
      <Icon
        size='small'
        color={ColorNames.Gray}
        name='info-circle'
        fitted
        style={{ verticalAlign: 'super' }}
        iconStyle='solid'
      />
    }
  />
)

const SealedBidForm = ({
  listing,
  slots,
  bid,
  counterOffer,
  className,
  readOnly = false,
  dirty = false,
  config: configProp,
  headers,
  onSubmit,
  onToggleReadOnly,
  viewingFrom = graph.enums.CurrentTargetAudience.Seller,
  formRef,
}: BidFormProps) => {
  const bidValues = useMemo(() => {
    const bids = counterOffer ? [bid, counterOffer] : [bid]
    return bids.map(bid => getSealedBidFormValues(bid, viewingFrom))
  }, [bid, counterOffer])

  const hasComponents = !!slots?.components
  const hasCounterOffer = counterOffer && !_.isEmpty(counterOffer)
  const counterDifferenceKeys = hasCounterOffer
    ? getOfferAndCounterOfferDifferences(bidValues[0], bidValues[1])
    : []

  const config = useMemo(() => {
    if (!configProp)
      return getSealedBidFormConfig({
        headers,
        listing,
        hasCounterOffer,
        canUpdateMyOffer: hasComponents,
        viewingFrom,
      })
    if (headers && headers.length) {
      return {
        ...configProp,
        headers: getSealedBidFormHeaders(
          headers,
          hasCounterOffer,
          hasComponents
        ),
      }
    }
    return configProp
  }, [configProp, headers, hasCounterOffer, hasComponents])

  const breakpoint = hooks.useBreakpoints()
  const isMobile = breakpoint === 'mobile'

  return (
    <div className={className}>
      <Form
        onSubmit={onSubmit}
        className={cx('traditional-offer-form', {
          'is-expanded': counterOffer && hasComponents,
          'my-offer-view': !counterOffer && !hasComponents,
        })}
        ref={formRef}
      >
        {isMobile && onToggleReadOnly && (
          <div className={cx('counter-offer-toggle')}>
            <CheckboxField
              checked={!readOnly || dirty}
              hideLabel
              onChange={onToggleReadOnly}
              disabled={dirty}
              toggle
            />
            <Text
              className='u-mb-2'
              color={ColorNames.Gray}
              size='tiny'
              weight={!readOnly || dirty ? 'heavy' : 'normal'}
            >
              Update Offer Instead
            </Text>
          </div>
        )}
        {config?.sections?.map((section, index) => {
          const headers = index === 0 ? config?.headers : []
          return (
            <div key={`section-${index}`} className={cx('section')}>
              <div className={cx('section-header')}>
                {(section.name || headers) && (
                  <Text className={cx('sealed-bid-title-column')} size='mini'>
                    {section.name && <span>{section.name}</span>}
                  </Text>
                )}
                {
                  // Hide the headers on mobile
                  !isMobile &&
                    headers?.map((header, i) => {
                      const isLastColumn =
                        index === 0 && headers && i === headers.length - 1
                      const showToggle =
                        isLastColumn && hasComponents && onToggleReadOnly
                      const isActive =
                        (isLastColumn &&
                          hasComponents &&
                          (!readOnly || dirty)) ||
                        !hasComponents
                      const isEditable = showToggle && !dirty
                      return (
                        <>
                          {isEditable && readOnly && (
                            <div
                              className={cx(
                                'sealed-bid-component-column',
                                'backdrop'
                              )}
                              onClick={onToggleReadOnly}
                            />
                          )}
                          <div
                            className={cx(
                              isLastColumn
                                ? 'sealed-bid-component-column'
                                : 'sealed-bid-header-column',
                              {
                                editable: isEditable,
                                active: isActive,
                                disabled: isLastColumn && !isActive,
                              }
                            )}
                            onClick={
                              isActive && !dirty ? onToggleReadOnly : undefined
                            }
                          >
                            {showToggle && (
                              <CheckboxField
                                checked={!readOnly || dirty}
                                hideLabel
                                onChange={onToggleReadOnly}
                                disabled={dirty}
                                toggle
                              />
                            )}
                            <Text
                              className={cx(
                                isLastColumn && !showToggle ? 'header-text' : ''
                              )}
                              key={i}
                              size={showToggle ? 'tiny' : 'small'}
                              color={ColorNames.Gray}
                              weight={
                                showToggle && (!readOnly || dirty)
                                  ? 'heavy'
                                  : 'medium'
                              }
                            >
                              {header}
                            </Text>
                          </div>
                        </>
                      )
                    })
                }
              </div>
              {section.fields.map(field => {
                const component = hasComponents
                  ? slots?.components?.({ name: [field.name] })
                  : null
                const isAdditionalNotesField = field.name === 'additional_notes'
                return (
                  <div
                    className={cx('row', {
                      'notes-row': isAdditionalNotesField,
                      'u-mb-2': isMobile && hasCounterOffer,
                    })}
                  >
                    <Text
                      className={cx('label-column', {
                        'full-width': isMobile && !readOnly && !hasCounterOffer,
                      })}
                      elmId={`${field.name}_label`}
                      weight='medium'
                    >
                      {field.label}
                      {field.toolTipContent && (
                        <span className='u-ml-1'>
                          <ToolTip content={field.toolTipContent} />
                        </span>
                      )}
                    </Text>

                    {bidValues.map((bidValue, index) => {
                      const suffix =
                        index === 1 && counterOffer ? '_counteroffer' : ''

                      const hasDifferences = counterDifferenceKeys.includes(
                        field.name
                      )

                      // To avoid rendering the my offer value column on mobile when the form is editable (NOT in read only mode)
                      // and the counter offer value column when the form is read only and the counter offer value is the same as the my offer value
                      if (
                        isMobile &&
                        ((!readOnly &&
                          ((hasCounterOffer && !suffix) || !hasCounterOffer)) ||
                          (readOnly && suffix && !hasDifferences))
                      ) {
                        return null
                      }

                      const textProps: any = {
                        key: `${field.name}-${index}`,
                        className: cx('value-column', 'last-column-label', {
                          'full-width': suffix && isMobile && readOnly,
                          'highlight-counter-offer': suffix && hasDifferences,
                          'highlight-my-offer': !suffix && hasDifferences,
                        }),
                        color:
                          !isMobile && suffix
                            ? ColorNames.GrayDark
                            : ColorNames.Gray,
                        elmId: `${field.name}_value${suffix}`,
                        weight:
                          isMobile ||
                          (suffix && hasDifferences && !hasComponents)
                            ? 'heavy'
                            : 'medium',
                      }

                      if (isAdditionalNotesField) {
                        const penIcon = (
                          <Icon
                            name='pen-to-square'
                            iconStyle='solid'
                            color={ColorNames.Info}
                          />
                        )
                        return (
                          <TextTruncate
                            {...textProps}
                            lines={1}
                            readMoreLabel={penIcon}
                            readLessLabel={penIcon}
                          >
                            {bidValue[field.name]?.value}
                          </TextTruncate>
                        )
                      }

                      return (
                        <Text {...textProps}>
                          {bidValue[field.name]?.value}
                        </Text>
                      )
                    })}
                    {!component || (isMobile && readOnly) ? null : (
                      <div
                        className={cx('component-column', {
                          'full-width': !readOnly,
                        })}
                        data-elm-id={`${field.name}_component`}
                      >
                        {component}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )
        })}
      </Form>
    </div>
  )
}

export default SealedBidForm
